/**
 * HS Code 분류 패널 — backend /api/hs-classify proxy → hs_classifier FastAPI 호출.
 *
 * 사용법: /api/hs-chapters 로딩 후 chapter 선택 + line_item 입력 → /api/hs-classify
 */

import React, { useEffect, useState } from "react";
import {
  Search,
  Loader,
  AlertCircle,
  CheckCircle,
  Layers,
  Edit3,
  Upload,
  Download,
} from "lucide-react";
import EvidenceCards from "../audit/EvidenceCards";
import "../audit/theme.css";

export default function HSClassifierPanel({ apiBaseUrl = "" }) {
  const [chapters, setChapters] = useState({});
  const [chapter, setChapter] = useState("auto");
  const [lineItem, setLineItem] = useState("");
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  // review (정정) UI 상태
  const [reviewOpen, setReviewOpen] = useState(false);
  const [reviewHsk, setReviewHsk] = useState("");
  const [reviewNote, setReviewNote] = useState("");
  const [reviewSaved, setReviewSaved] = useState(false);
  const [reviewError, setReviewError] = useState(null);
  // batch
  const [mode, setMode] = useState("single"); // 'single' or 'batch'
  const [batchText, setBatchText] = useState("");
  const [batchResults, setBatchResults] = useState(null);
  const [batchLoading, setBatchLoading] = useState(false);

  useEffect(() => {
    fetch(`${apiBaseUrl}/api/hs-chapters`)
      .then((r) => r.json())
      .then((d) => {
        const desc = d?.descriptions || {};
        setChapters(desc);
        const first = Object.keys(desc)[0];
        if (first) setChapter(first);
      })
      .catch((err) => setError(`chapters 로드 실패: ${err.message}`));
  }, [apiBaseUrl]);

  async function classify() {
    if (!lineItem.trim()) return;
    setLoading(true);
    setError(null);
    setResult(null);
    setReviewOpen(false);
    setReviewSaved(false);
    setReviewError(null);
    setReviewHsk("");
    setReviewNote("");
    try {
      // v2 endpoint: Dual-Evidence + audit_evidence (audit=true 강제)
      const r = await fetch(`${apiBaseUrl}/api/hs-classify-v2`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          line_item: lineItem.trim(),
          chapter,
          audit: true,
        }),
      });
      const data = await r.json();
      if (!r.ok) {
        setError(data?.detail || data?.error || `HTTP ${r.status}`);
      } else {
        setResult(data);
        if (data.hsk_10digit) setReviewHsk(data.hsk_10digit);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  async function downloadPdf() {
    if (!lineItem.trim()) return;
    try {
      const r = await fetch(`${apiBaseUrl}/api/hs-classify-v2/pdf`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          line_item: lineItem.trim(),
          chapter,
          audit: true,
        }),
      });
      if (!r.ok) {
        alert("PDF 생성 실패");
        return;
      }
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `hs-audit-${result?.hs_6digit || "result"}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      alert(`PDF 생성 실패: ${err.message}`);
    }
  }

  function openPrecedents() {
    // 전체 선례 검색 — query param 으로 처리 (별도 URL/nav 생성 안 함)
    try {
      const url = new URL(window.location.href);
      url.searchParams.set("audit", "precedents");
      window.history.pushState({}, "", url.toString());
      window.dispatchEvent(new PopStateEvent("popstate"));
    } catch (_) {
      window.location.search = "?audit=precedents";
    }
  }

  async function runBatch() {
    const lines = batchText
      .split(/\r?\n/)
      .map((s) => s.trim())
      .filter((s) => s.length > 0);
    if (lines.length === 0) return;
    if (lines.length > 500) {
      alert("500 줄 초과 — 분할 권장");
      return;
    }
    setBatchLoading(true);
    setBatchResults(null);
    try {
      const r = await fetch(`${apiBaseUrl}/api/hs-classify/batch`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ items: lines, default_chapter: chapter }),
      });
      const data = await r.json();
      setBatchResults(data);
    } catch (err) {
      alert(err.message);
    } finally {
      setBatchLoading(false);
    }
  }

  function downloadBatchCSV() {
    if (!batchResults?.results) return;
    const header = [
      "no",
      "line_item",
      "predicted_hsk",
      "hs_6digit",
      "hs_4digit",
      "chapter",
      "detected_chapter",
      "confidence",
      "requires_review",
      "reasoning",
    ];
    const rows = batchResults.results.map((r) => [
      r.index + 1,
      r.line_item ?? "",
      r.hsk_10digit ?? "",
      r.hs_6digit ?? "",
      r.hs_4digit ?? "",
      r.chapter ?? r.detected_chapter ?? "",
      r.detected_chapter ?? "",
      r.confidence ?? (r.ok ? "" : "error"),
      r.ok ? (r.requires_review ? "Y" : "N") : "error",
      (r.reasoning ?? r.error ?? "").replace(/"/g, '""'),
    ]);
    const csv = [header, ...rows]
      .map((row) => row.map((v) => `"${v}"`).join(","))
      .join("\n");
    const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `hs_classify_batch_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  function loadCsvFile(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const text = ev.target.result;
      // CSV 파싱: 첫 행이 헤더인지 확인 후 description/품명 컬럼 추출, 없으면 첫 컬럼
      const lines = text.split(/\r?\n/).filter((l) => l.trim());
      if (lines.length === 0) return;

      // 탭 or 쉼표 구분자 감지
      const sep = lines[0].includes("\t") ? "\t" : ",";
      const parseRow = (row) =>
        row.split(sep).map((c) => c.trim().replace(/^"|"$/g, "").trim());

      const headers = parseRow(lines[0]).map((h) => h.toLowerCase());
      const descIdx = headers.findIndex((h) =>
        /description|품명|item|product|goods|상품명/i.test(h),
      );
      const composIdx = headers.findIndex((h) =>
        /composit|composition|재질|성분/i.test(h),
      );

      let descs;
      if (descIdx >= 0) {
        // 헤더 행 있음 — description 컬럼 추출
        descs = lines
          .slice(1)
          .map((l) => {
            const cols = parseRow(l);
            const desc = cols[descIdx] || "";
            const comp = composIdx >= 0 ? cols[composIdx] || "" : "";
            return comp ? `${desc} ${comp}`.trim() : desc;
          })
          .filter(Boolean);
      } else {
        // 헤더 없음 — 첫 컬럼 그대로 사용
        descs = lines.map((l) => parseRow(l)[0] || "").filter(Boolean);
      }
      setBatchText(descs.join("\n"));
    };
    reader.readAsText(file, "utf-8");
    e.target.value = "";
  }

  async function submitReview() {
    setReviewError(null);
    if (!result?.audit_id) {
      setReviewError("audit_id 부재");
      return;
    }
    if (!/^\d{10}$/.test(reviewHsk)) {
      setReviewError("HSK 10자리 (숫자만)");
      return;
    }
    try {
      const r = await fetch(`${apiBaseUrl}/api/hs-classify/review`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          audit_id: result.audit_id,
          reviewed_hsk: reviewHsk,
          review_note: reviewNote || null,
        }),
      });
      const data = await r.json();
      if (!r.ok) {
        setReviewError(data?.detail || data?.error || `HTTP ${r.status}`);
      } else {
        setReviewSaved(true);
      }
    } catch (err) {
      setReviewError(err.message);
    }
  }

  const samples = {
    33: "SKIN CARE COSMETIC, ANTI-AGING CREAM 50ML",
    39: "PLASTIC CUP, PET 0.95OZ",
    42: "BACKPACK, NYLON LEATHER",
    64: "MEN LEATHER SHOES, 240MM",
    84: "PARTS FOR MOTOR VEHICLE, BEARING ASSY",
    85: "LCD MONITOR, 27INCH",
    86: "RAILWAY LOCOMOTIVE PARTS, BRAKE SHOE",
  };

  return (
    <div className="h-full overflow-auto bg-[#F5F1EC] p-6">
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Search className="w-7 h-7 text-blue-600" />
            <h1 className="text-2xl font-semibold text-gray-900">
              HS Code 분류
            </h1>
          </div>
          <div className="flex bg-white border border-gray-200 rounded-md overflow-hidden">
            <button
              onClick={() => setMode("single")}
              className={`px-3 py-1.5 text-sm ${mode === "single" ? "bg-blue-600 text-white" : "text-gray-700 hover:bg-gray-50"}`}
            >
              단건
            </button>
            <button
              onClick={() => setMode("batch")}
              className={`px-3 py-1.5 text-sm ${mode === "batch" ? "bg-blue-600 text-white" : "text-gray-700 hover:bg-gray-50"}`}
            >
              일괄 (Batch)
            </button>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 space-y-4">
          {/* Chapter selector */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Chapter (HS류)
            </label>
            <select
              value={chapter}
              onChange={(e) => {
                setChapter(e.target.value);
                if (samples[e.target.value] && !lineItem)
                  setLineItem(samples[e.target.value]);
              }}
              className="w-full max-w-xs rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="auto">🤖 AUTO — 자동 감지</option>
              {Object.entries(chapters).map(([ch, desc]) => (
                <option key={ch} value={ch}>
                  {ch}류 — {desc}
                </option>
              ))}
            </select>
          </div>

          {/* line_item input — single 모드 */}
          {mode === "single" && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  인보이스 line item
                  <button
                    type="button"
                    className="ml-2 text-xs text-blue-600 hover:underline"
                    onClick={() => setLineItem(samples[chapter] || "")}
                  >
                    예시 사용
                  </button>
                </label>
                <textarea
                  value={lineItem}
                  onChange={(e) => setLineItem(e.target.value)}
                  rows={3}
                  placeholder={samples[chapter] || "Line item 입력..."}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono"
                />
              </div>
              <button
                onClick={classify}
                disabled={loading || !lineItem.trim()}
                className="px-4 py-2 rounded-md bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {loading ? (
                  <Loader className="w-4 h-4 animate-spin" />
                ) : (
                  <Search className="w-4 h-4" />
                )}
                {loading ? "분류 중…" : "분류하기"}
              </button>
            </>
          )}

          {/* batch mode */}
          {mode === "batch" && (
            <>
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-sm font-medium text-gray-700">
                    인보이스 line items (한 줄당 1개, 최대 500줄)
                  </label>
                  <label className="flex items-center gap-1.5 px-2 py-1 text-xs rounded border border-gray-300 cursor-pointer hover:bg-gray-50 text-gray-600">
                    <Upload className="w-3.5 h-3.5" />
                    CSV/TSV 불러오기
                    <input
                      type="file"
                      accept=".csv,.tsv,.txt"
                      className="hidden"
                      onChange={loadCsvFile}
                    />
                  </label>
                </div>
                <textarea
                  value={batchText}
                  onChange={(e) => setBatchText(e.target.value)}
                  rows={10}
                  placeholder={
                    "LIPSTICK MATTE RED\nWOODEN OFFICE DESK\nMRI APPARATUS\n\n또는 CSV 파일 업로드 (description/품명 컬럼 자동 감지)"
                  }
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono"
                />
                <div className="text-xs text-gray-500 mt-1">
                  {batchText.split(/\r?\n/).filter((s) => s.trim()).length} 줄 ·
                  chapter: {chapter === "auto" ? "🤖 자동감지" : chapter}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={runBatch}
                  disabled={batchLoading || !batchText.trim()}
                  className="px-4 py-2 rounded-md bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {batchLoading ? (
                    <Loader className="w-4 h-4 animate-spin" />
                  ) : (
                    <Layers className="w-4 h-4" />
                  )}
                  {batchLoading ? "처리 중…" : "일괄 분류"}
                </button>
                {batchResults && (
                  <button
                    onClick={downloadBatchCSV}
                    className="px-3 py-2 rounded-md border border-gray-300 text-sm flex items-center gap-1.5 hover:bg-gray-50"
                  >
                    <Download className="w-4 h-4" />
                    CSV 다운로드
                  </button>
                )}
                {batchResults && (
                  <span className="ml-auto text-xs text-gray-500">
                    성공 {batchResults.success}/{batchResults.total} · review{" "}
                    {batchResults.requires_review} · high{" "}
                    {batchResults.confidence_distribution?.high || 0}
                  </span>
                )}
              </div>
              {batchResults && (
                <div className="mt-3 overflow-x-auto max-h-96 border border-gray-200 rounded-md">
                  <table className="w-full text-xs border-collapse">
                    <thead className="sticky top-0 bg-gray-100 text-gray-600">
                      <tr>
                        <th className="px-2 py-1.5 text-left border-b border-gray-200 font-semibold w-8">
                          #
                        </th>
                        <th className="px-2 py-1.5 text-left border-b border-gray-200 font-semibold">
                          품목
                        </th>
                        <th className="px-2 py-1.5 text-left border-b border-gray-200 font-semibold w-28">
                          HSK 10자리
                        </th>
                        <th className="px-2 py-1.5 text-left border-b border-gray-200 font-semibold w-12">
                          류
                        </th>
                        <th className="px-2 py-1.5 text-left border-b border-gray-200 font-semibold w-16">
                          신뢰도
                        </th>
                        <th className="px-2 py-1.5 text-left border-b border-gray-200 font-semibold w-14">
                          검토
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {batchResults.results.map((r) => (
                        <tr
                          key={r.index}
                          className={`border-b border-gray-100 ${!r.ok ? "bg-rose-50" : r.requires_review ? "bg-amber-50" : ""}`}
                        >
                          <td className="px-2 py-1 font-mono text-gray-400">
                            {r.index + 1}
                          </td>
                          <td
                            className="px-2 py-1 max-w-xs truncate"
                            title={r.line_item}
                          >
                            {r.line_item}
                          </td>
                          <td className="px-2 py-1 font-mono font-semibold">
                            {r.hsk_10digit || (
                              <span className="text-rose-500">
                                {r.error?.slice(0, 25)}
                              </span>
                            )}
                          </td>
                          <td className="px-2 py-1 font-mono text-gray-500">
                            {r.chapter || r.detected_chapter || "-"}
                          </td>
                          <td className="px-2 py-1">
                            <span
                              className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                                r.confidence === "high"
                                  ? "bg-emerald-100 text-emerald-700"
                                  : r.confidence === "medium"
                                    ? "bg-amber-100 text-amber-700"
                                    : r.confidence === "low"
                                      ? "bg-rose-100 text-rose-700"
                                      : "bg-gray-100 text-gray-500"
                              }`}
                            >
                              {r.confidence || "-"}
                            </span>
                          </td>
                          <td className="px-2 py-1 text-center">
                            {r.ok && r.requires_review ? "⚠" : ""}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>

        {error && (
          <div className="mt-4 bg-rose-50 border border-rose-200 rounded-md p-3 flex items-start gap-2 text-sm text-rose-800">
            <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <div>{error}</div>
          </div>
        )}

        {result && (
          <>
            {/* Audit Evidence (Dual-Evidence v2): 결정 + 법령 + 선례 + PDF */}
            <div className="mt-4 bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <EvidenceCards
                response={result}
                onDownloadPdf={downloadPdf}
                onOpenPrecedents={openPrecedents}
              />
            </div>

            {/* 정정 버튼 + 학습 데이터화 (audit_id 가 있을 때만) */}
            {result.audit_id && (
              <div className="mt-4 bg-white rounded-xl shadow-sm border border-gray-200 p-5">
                {!reviewOpen && !reviewSaved && (
                  <button
                    onClick={() => setReviewOpen(true)}
                    className="text-xs text-blue-600 hover:underline flex items-center gap-1"
                  >
                    <Edit3 className="w-3 h-3" />
                    분류가 잘못됐나요? 정정하기
                  </button>
                )}
                {reviewSaved && (
                  <div className="flex items-center gap-2 text-xs text-emerald-700">
                    <CheckCircle className="w-3.5 h-3.5" />
                    정정 저장됨. 학습 데이터로 사용됩니다.
                  </div>
                )}
                {reviewOpen && !reviewSaved && (
                  <div className="space-y-2 bg-amber-50/50 border border-amber-200 rounded p-3">
                    <div className="text-xs text-amber-900 font-medium">
                      관세사 정정 (학습 데이터화)
                    </div>
                    <div>
                      <label className="block text-xs text-gray-700 mb-0.5">
                        정정 HSK 10자리
                      </label>
                      <input
                        type="text"
                        value={reviewHsk}
                        onChange={(e) =>
                          setReviewHsk(
                            e.target.value.replace(/\D/g, "").slice(0, 10),
                          )
                        }
                        className="w-48 rounded border border-gray-300 px-2 py-1 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-amber-400"
                        placeholder="1234567890"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-700 mb-0.5">
                        메모 (optional)
                      </label>
                      <input
                        type="text"
                        value={reviewNote}
                        onChange={(e) => setReviewNote(e.target.value)}
                        className="w-full rounded border border-gray-300 px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
                        placeholder="정정 사유 / 참고 사항"
                      />
                    </div>
                    {reviewError && (
                      <div className="text-xs text-rose-700">{reviewError}</div>
                    )}
                    <div className="flex gap-2">
                      <button
                        onClick={submitReview}
                        className="px-3 py-1 rounded bg-amber-600 text-white text-xs hover:bg-amber-700"
                      >
                        저장
                      </button>
                      <button
                        onClick={() => {
                          setReviewOpen(false);
                          setReviewError(null);
                        }}
                        className="px-3 py-1 rounded border border-gray-300 text-xs text-gray-700 hover:bg-gray-50"
                      >
                        취소
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </>
        )}

        {/* Stage 2 후보 + 점수 (boundary 분석용) */}
        {result?.candidates?.length > 0 && (
          <details className="mt-4 bg-white rounded-xl shadow-sm border border-gray-200 p-5">
            <summary className="font-semibold text-sm text-gray-900 flex items-center gap-2 cursor-pointer">
              <Layers className="w-4 h-4 text-blue-600" />
              후보 ({result.candidates.length})
            </summary>
            <table className="mt-3 w-full text-xs">
              <thead>
                <tr className="text-left text-gray-500 border-b border-gray-200">
                  <th className="py-1 pr-2">HS6</th>
                  <th className="py-1 pr-2">설명</th>
                  <th className="py-1 pr-2 text-right">rule</th>
                  <th className="py-1 pr-2 text-right">bm25</th>
                  <th className="py-1 text-right">final</th>
                </tr>
              </thead>
              <tbody>
                {result.candidates.map((c, i) => (
                  <tr
                    key={i}
                    className="border-b border-gray-100 last:border-0"
                  >
                    <td className="py-1 pr-2 font-mono">{c.hs_code_6digit}</td>
                    <td className="py-1 pr-2 text-gray-700 max-w-md truncate">
                      {c.description_ko}
                    </td>
                    <td className="py-1 pr-2 text-right font-mono">
                      {c.rule_score.toFixed(2)}
                    </td>
                    <td className="py-1 pr-2 text-right font-mono">
                      {c.bm25_score.toFixed(2)}
                    </td>
                    <td className="py-1 text-right font-mono font-semibold">
                      {c.final_score.toFixed(2)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </details>
        )}

        <div className="mt-6 text-xs text-gray-500">
          <p>
            <CheckCircle className="w-3 h-3 inline mr-1" />
            지원 chapter: {Object.keys(chapters).join(", ")}
          </p>
          <p className="mt-1">
            법령·선례 이중 근거 기반 세번분류 엔진 (Dual-Evidence). Gemini 3
            Flash 기반.
          </p>
        </div>
      </div>
    </div>
  );
}
